import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, Chip, Stack } from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';

import useDataGridPagination from '~/hooks/mui/useDataGridPagination';

import CorporateEventsService, { InvitedUser } from '~/services/CorporateEventsService';

interface InvitedUsersListProps {
  eventId?: string;
  invitedUsers: Array<InvitedUser>;
  onEditClick: (invitedUser: InvitedUser) => void;
  onCreateClick: () => void;
  onDeleteClick: (emails: Array<string>) => void;
  onImportClick?: () => void;
}

export default function InvitedUsersList({
  eventId,
  invitedUsers = [],
  onEditClick,
  onCreateClick,
  onDeleteClick,
  onImportClick,
}: InvitedUsersListProps) {
  const { paginationModel, setPaginationModel } = useDataGridPagination({
    pageSize: 25,
    page: 0,
  });

  const [selectedRows, setSelectedRows] = useState<GridRowSelectionModel>([]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['corporate-events', 'invited-users', eventId],
    queryFn: () => CorporateEventsService.getInvitedUsersByEventId(eventId),
    enabled: !!eventId,
  });

  const allInvitedUsers = eventId ? data || [] : invitedUsers;

  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setSelectedRows(newSelection);
  };

  const handleDeleteSelected = () => {
    if (selectedRows.length === 0) {
      return;
    }

    const emailsToDelete = selectedRows
      .map((rowId) => {
        const user = allInvitedUsers.find((u) => u.id === rowId || u.email === rowId);
        return user?.email;
      })
      .filter((email): email is string => !!email);

    if (emailsToDelete.length > 0) {
      onDeleteClick(emailsToDelete);
      setSelectedRows([]);
    }
  };

  const columns: Array<GridColDef<InvitedUser>> = [
    {
      field: 'email',
      headerName: 'Email',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'fullName',
      headerName: 'Full Name',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'role',
      headerName: 'Role',
      flex: 1,
      minWidth: 120,
    },
    {
      field: 'hasAnalyticsAccess',
      headerName: 'Analytics Access',
      width: 150,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          {params.row.hasAnalyticsAccess ? (
            <Chip label="Yes" color="primary" size="small" />
          ) : (
            <Chip label="No" size="small" />
          )}
        </Box>
      ),
    },
    {
      field: 'invitedAt',
      headerName: 'Invited At',
      width: 150,
      valueGetter: (value) => {
        if (!value) {
          return '';
        }
        return new Date(value).toLocaleDateString();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 130,
      getActions: (params) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon />}
          label="Edit"
          onClick={() => onEditClick(params.row)}
          color="inherit"
        />,
      ],
    },
  ];

  return (
    <Box>
      {!!error && <Alert severity="error">{error?.message}</Alert>}
      <Box mb={4} display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          {selectedRows.length > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
              sx={{ mr: 2 }}
            >
              Delete Selected ({selectedRows.length})
            </Button>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          {eventId && onImportClick && (
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={onImportClick}>
              Import from File
            </Button>
          )}
          <Button variant="contained" startIcon={<AddIcon />} onClick={onCreateClick}>
            Add User
          </Button>
        </Stack>
      </Box>
      <DataGrid
        loading={isLoading && !!eventId}
        rows={allInvitedUsers}
        columns={columns}
        pagination
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        getRowId={(row: InvitedUser) => row.id || row.email}
        paginationMode="client"
        rowCount={allInvitedUsers.length}
        slots={{
          pagination: GridPagination,
        }}
        checkboxSelection
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={handleSelectionChange}
        autoHeight
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />
    </Box>
  );
}
